import type { ProviderDefinition } from "fileParsing/types";
import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import { Button, Grid, Stack, Typography } from "@mui/material";
import { Box } from "@mui/system";
import type { ParsedExport } from "@store/data/types";
import { clearLastFilename } from "@store/fileParsing/actions";
import { setSidebarFilter } from "@store/filters/actions";
import { setCurrentVisualization } from "@store/options/actions";
import { VisTypes } from "@store/options/types";
import Dropzone from "containers/Components/Dropzone";
import useTranslations from "containers/hooks/useLanguageHook";
import { useAppDispatch, useAppSelector } from "index";
import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { ApplicationState } from "store/index";
import ProviderItemExpanded from "../Components/ProviderItemExpanded";
import { ProviderSelectItem } from "../Components/ProviderSelectItem";

function DataPage() {
  const providerItems = useAppSelector((state: ApplicationState) => state.data.exports);
  const providers = useAppSelector((state: ApplicationState) => state.data.providers);

  const dispatch = useAppDispatch();
  const { t } = useTranslations();
  const params = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(setSidebarFilter([]));
    dispatch(setCurrentVisualization(VisTypes.All));
  }, [dispatch]);

  if (params.providerId)
    return (
      <Box sx={{ pb: 12 }}>
        <Stack direction="row" spacing={2} sx={{ pl: 5, pt: 2, pb: 2 }}>
          <Button
            variant="contained"
            onClick={() => {
              dispatch(clearLastFilename());
              navigate(-1);
            }}
          >
            <ArrowBackIcon /> {t.DataPage.Back}
          </Button>
        </Stack>
        <ProviderItemExpanded exportId={params.providerId} />
      </Box>
    );

  return (
    <Box sx={{ pb: 12 }}>
      <Stack
        direction="column"
        sx={{
          alignItems: "center",
          pt: 2,
          maxWidth: 1280,
          margin: "auto",
          paddingBottom: "4em",
        }}
      >
        <Grid item xs={12}>
          <Typography variant="h3">{t.DataPage.WhichService}</Typography>
        </Grid>
        <Grid item xs={12} container direction="row" justifyContent="center" alignItems="flex-start">
          {providers
            .sort((a, b) => (b.id === "sample" ? -1 : a.name.localeCompare(b.name)))
            .map((d) => {
              const addedExportsForProvider = providerItems.filter(
                (providerExport) => providerExport.providerId === d.id,
              );

              return renderProvider(d, addedExportsForProvider);
            })}
        </Grid>

        <Grid
          item
          xs={12}
          sx={{
            mt: 2,
          }}
        >
          <Stack direction={"row"}>
            <Typography variant="subtitle2" sx={{ color: "#666" }}>
              {t.DataPage.Tip}
            </Typography>
          </Stack>
        </Grid>

        <Box
          sx={{
            paddingX: {
              xs: "1rem",
              md: "20%",
            },
            height: "12rem",
            mt: 1,
            mb: 3,
          }}
        >
          <Dropzone />
        </Box>
      </Stack>
    </Box>
  );

  function setActive(def: ProviderDefinition) {
    navigate(`data/${def.id}`);
  }

  function renderProvider(def: ProviderDefinition, exports: ParsedExport[]) {
    const files = providerItems.filter((d) => d.providerId === def.id);

    if (def.hidden === true && files.length === 0) return null;

    return exports.length === 0 ? (
      <Grid key={def.id} item>
        <ProviderSelectItem item={def} onClick={() => setActive(def)} />
      </Grid>
    ) : (
      exports.map((providerExport) => (
        <Grid key={`${def.id}-${providerExport.id}`} item>
          <ProviderSelectItem item={def} export={providerExport} />
        </Grid>
      ))
    );
  }
}

export { DataPage };
